import {EventEmitter, Injectable} from '@angular/core';
import {AztecScannerResult} from '../enums/next/aztec-scanner-result.enum';
import {AztecScannerApiService} from './aztec-scanner-api.service';
import {AztecScannerLibDataModel} from '../models/aztec-scanner-library.model';
import {ApplicationStateService} from './application-state.service';
import {SessionService} from '../../core/services/impl/session/session.service';
import {AztecScannerService} from './aztec-scanner.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {CustomMetricsName} from '../components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../components/models/metrics/metrics-type.enum';
import {env} from '../../core/utils/utils';
import {RuntimeEnvironment} from '../enums/runtime-environment.enum';
import {MetricsService} from './metrics.service';

declare const Neptis: any;

@Injectable({
  providedIn: 'root'
})
export class NextAztecScannerService {

  readonly CAMERA_CONTAINER_ID = 'cameraContainer';
  readonly DOWNLOAD_VEHICLE_HISTORY_FROM_SERVER = false;
  readonly API_KEY: string = '2dd6a017-aa16-4473-b8db-1c41f1ffa6d9';
  readonly AZTEC_DECODER_LIB_URL = 'https://aztec.neptis.pl/js/aztec-decoder.js';

  aztecScannerResult$: EventEmitter<AztecScannerResult> = new EventEmitter<AztecScannerResult>();
  aztecScannerResult: AztecScannerResult;

  public decoder: any;
  public pendingRequest = false;
  public instance = this;
  public cameraErrorMode = false;
  public waitingForDataMode = false;
  public cameraViewInitialMode = false;
  public loadingLib = false;
  private aztecDecoderLibInitialized = false;
  private consoleLogsEnabled = false;

  constructor(private aztecScannerApiService: AztecScannerApiService,
              private aztecScannerService: AztecScannerService,
              private stateService: ApplicationStateService,
              private sessionService: SessionService,
              private metricsService: MetricsService) { }

  async initScanner() {
    await this.loadAztecDecoderLibrary();
    setTimeout(() => this.initCamera());
  }

  public stopScanning(): void {
    try {
      this.decoder.stop();
    } catch (e) {
      console.log(e);
    }
  }

  mock(): void {
    this.onDecodedSuccessHandler({
      aztec: 'XXC1|BAK3154368|2203|STAROSTA CZŁUCHOWSKI|ul. WOJSKA POLSKIEGO 1|77-300 CZŁUCHÓW||GCZ 97XX|VOLKSWAGEN||||GOLF|WVWZZZ1HZVP196718|2014-08-12|---|TWIERKOWSKI ADAM STANISŁAW|ADAM STANISŁAW|TWIERKOWSKI||66033006439|77-310|DEBRZNO|DEBRZNO|OSIEDLE 35-LECIA PRL|12A|6|TWIERKOWSKI ADAM STANISŁAW|ADAM STANISŁAW|TWIERKOWSKI||66033006439|77-310|DEBRZNO|DEBRZNO|OSIEDLE 35-LECIA PRL|12A|6|1505|1505|---|1000|---|---|2|800|500|---|1390,00|44,00|P |1996-10-30|5|---|SAMOCHÓD OSOBOWY|---|1996|---|7,90|AAB5958784|02170020000000|03|02|000|0000NNNNNNNN|009002007|',
      aztecEncoded: 'RAQAANtYAAJDAP8xAHwAQgBBAO9LADMWNd8ANA42AL04KjLuAjAa3RJTAFT7QlIATxN3IG62Wg4BVXtIKt1XLq9+SVJ19wBsAC4+vFcqSrdbXlAaTN9HRQBHexog7+83Ai1+wjAAMMWn3tOCfPcCRyu1NjnBUl+aIlb7l0ua3le2R/2eTgB8uwNHN+9GEld+TlcAWvsDMZ7eWh5Q7RKONniSMS6/MPASNHcCMB69LRYy7yotA2sOVH6DW3BSAEtXwyB75kQG8E0AIN0XTtgqEhRuQR1jojtYpyQaczbhAsHXmBc2vAs5NUPeMSp87bZ2Qnt6Wq7aTx+xZz+/NS5M7wogbjXfAC0XQ8UASRN3Uh6DfNfeQe8K04gUbr8wADXBE4rfF+92MAAwI7YPEesrODd2NUcuMQ571iIsL/s0ADQXH1AAIHxDOX7rLfTDTdt8g/OHU4/RT+93RGoGu2gGQn1zWYPbZZenIzcAvCz/Wh9B92I1GmsGOC61BjTaKjIy8Go3fiMyADDBA9V8u24ztXwnswNOIWwz2jmT7jcmEkkkklT/H'
    });
  }

  protected onVideoInitializedSuccessHandler(): void {
    // Camera initialized, waiting for code scanning
    this.log('VideoInitializedSuccess');
    this.sendNeptisCallbackMetric('VideoInitializedSuccess');
    this.aztecScannerService.handleAztecScannedWebsocket();
  };

  protected onScanSuccessHandler(): void {
    // Success aztec decoded with library
    this.waitingForDataMode = true;
    this.log('ScanSuccess');
    this.sendNeptisCallbackMetric('ScanSuccess');
  };

  protected onVideoInitializedErrorHandler(data: any): void {
    this.log('VideoInitializedError', data);
    this.sendNeptisCallbackMetric('VideoInitializedError', data);
  };

  protected onDecodedErrorHandler(message: any): void {
    this.log('DecodedError', message);
    this.sendNeptisCallbackMetric('DecodedError', message);
  };

  protected sendNeptisCallbackMetric(callback: string, callbackData?: any): void {
    const data: any = {
      callback: callback
    };
    if (isNotNullOrUndefined(callbackData)) {
      data.callbackData = callbackData
    }
    this.metricsService.sendSimpleMetric(CustomMetricsName.AZTEC_SCANNER_NEPTIS_CALLBACK, MetricsType.PROCESS, JSON.stringify(data));
  }

  protected log(message: string, data?: any): void {
    if (this.consoleLogsEnabled) {
      console.log(`Neptis:${message}`, data);
    }
  }

  private initCamera(): void {
    try {
      this.decoder = new Neptis.RegistrationCertificateDecoder(
          this.API_KEY,
          this.CAMERA_CONTAINER_ID,
          this.DOWNLOAD_VEHICLE_HISTORY_FROM_SERVER,
          (instance: any) => this.onVideoInitializedSuccessHandler(),
          (data: any) => this.onVideoInitializedErrorHandler(data),
          (instance: any) => this.onScanSuccessHandler(),
          (data: AztecScannerLibDataModel) => this.onDecodedSuccessHandler(data),
          (message: any) => this.onDecodedErrorHandler(message)
      );

      this.decoder.start();
      this.pendingRequest = false;

    } catch (e) {
      console.log('initCamera error', e);
      this.cameraErrorMode = true;
      this.cameraViewInitialMode = false;
    }
  }

  private async onDecodedSuccessHandler(aztecData: AztecScannerLibDataModel) {
    this.log('DecodedSuccess', aztecData);
    this.sendNeptisCallbackMetric('DecodedSuccess', aztecData);
    if (!this.pendingRequest) {

      // create new session for aztec data to avoid overwriting existing session data
      if (this.stateService.state.calculationDataFromSession) {
        await this.sessionService.resetSession();
        this.aztecScannerService.resetAztecScannedWebsocket();
      }

      this.aztecScannerApiService.sendDecodedAztec(aztecData.aztecEncoded)
          .then(data => {
            this.waitingForDataMode = true;
            this.stopScanning();
            this.setAztecScannerResult(AztecScannerResult.SUCCESS);
          })
          .catch(err => {
            this.stopScanning();
            this.setAztecScannerResult(AztecScannerResult.ERROR);
          });
    }
    this.pendingRequest = true;
  };

  private setAztecScannerResult(result: AztecScannerResult): void {
    this.aztecScannerResult = result;
    this.aztecScannerResult$.emit(result);
  }

  private loadAztecDecoderLibrary(): Promise<void> {
    return new Promise(res => {
      if (!this.aztecDecoderLibInitialized) {
        this.loadingLib = true;
        const script = document.createElement('script');
        script.onload = () => {
          this.metricsService.sendSimpleMetric(CustomMetricsName.LOADING_SCRIPT_SUCCESS, MetricsType.PROCESS, this.AZTEC_DECODER_LIB_URL);
          this.aztecDecoderLibInitialized = true;
          this.loadingLib = false;
          res();
        };
        script.onerror = () => {
          this.metricsService.sendSimpleMetric(CustomMetricsName.LOADING_SCRIPT_ERROR, MetricsType.PROCESS, this.AZTEC_DECODER_LIB_URL);
        }
        script.src = this.AZTEC_DECODER_LIB_URL;
        document.head.appendChild(script);
      } else {
        res();
      }
    });
  }
}
